/**
 * The live front door (#26 / R6.1).
 *
 * `app/src/pages/Login.jsx` is presentational by contract: it performs no fetch and no navigation,
 * owns its own drafts, and calls `onSignIn({ email, password })`. This container supplies the real
 * behavior and nothing else, so the recovered surface keeps its design and its tests.
 *
 * This route is deliberately NOT fixture-backed. Every surface entered fixture mode before this
 * change, and `assertNoMutation` throws inside the API client, so a sign-in POST was refused inside
 * the browser and never reached the server. The preview gallery keeps that guarantee; the front
 * door cannot have it.
 *
 * Error copy is split on purpose. A rejected credential is the server's own sentence, because the
 * family needs to know it was the password. A fault is a calm, separate sentence, because telling a
 * family their password is wrong when the server is down sends them to reset a password that works.
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LanternLoader from '../components/LanternLoader.jsx';
import ResendVerificationAction from '../components/ResendVerificationAction.jsx';
import { ApiError, apiUrl } from '../api/client.js';
import { activateProfile, fetchProfiles, signIn, toChildActor } from '../api/session.js';
import { useActor } from '../state/ActorContext.jsx';

const Login = lazy(() => import('../pages/Login.jsx'));

/** Where a signed-in family lands. Today is the app's home surface, not the marketing page. */
export const LANDING_ROUTE = '/today';

const FAULT_MESSAGE = 'We could not sign you in just now — please try again in a moment.';

/**
 * #578 L17/L21 — what the PIN pad says. The server decides every one of these; the browser never
 * compares a PIN. A lock and a fault are NOT a wrong PIN, so neither may use the wrong-PIN words:
 * a child told their right PIN "did not work" stops trusting it.
 */
const PIN_REJECTED = 'That code did not work — try again.';
const PIN_FAULT = 'We could not check that code just now — please try again in a moment.';
/**
 * #580 D1 (#898) — shown before the first digit when the server says this tap starts a FAMILY TURN.
 * The server takes the device out of the parent's hands when the code is sent, before it is checked,
 * so a wrong code still ends the parent's sign-in (d-20260929-03, -04). No owner wording exists; this
 * is proposed copy for review.
 */
const TURN_WARNING = "Entering a code here ends the grown-up's sign-in on this device, even if the code is wrong. A grown-up will need to sign in again.";

function pinLockedMessage(retryAfterSeconds) {
  const minutes = Number.isFinite(retryAfterSeconds) ? Math.max(1, Math.ceil(retryAfterSeconds / 60)) : null;
  return minutes
    ? `Let's wait a moment — try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`
    : "Let's wait a moment before trying again.";
}

/**
 * Turn a thrown error into one sentence the family can act on.
 *
 * 401 and 403 are the server's own judgements about the credential, so its message is shown.
 * Everything else is a fault, and a fault must never read as "your password is wrong".
 */
export function signInMessage(error) {
  if (!(error instanceof ApiError)) return FAULT_MESSAGE;
  if (error.status === 0) return error.message;
  if (error.status === 401 || error.status === 403) {
    const fromBody = error.body?.error || error.body?.message;
    return typeof fromBody === 'string' && fromBody.trim() ? fromBody : FAULT_MESSAGE;
  }
  return FAULT_MESSAGE;
}

/**
 * #638 — the address to send a new verification link to, or null when there is nothing to offer.
 *
 * Only the server's "Email not verified" 403 says `canResendVerification`, and it names the
 * ACCOUNT's address (`auth.service.js`, after the password matched). That address is used rather
 * than the typed one, because a parent may sign in by username. A wrong password never gets here.
 */
export function resendAddressFrom(error) {
  if (!(error instanceof ApiError) || error.status !== 403) return null;
  if (error.body?.canResendVerification !== true) return null;
  const email = error.body?.email;
  return typeof email === 'string' && email.trim() ? email.trim() : null;
}

export default function LoginRoute() {
  const { actor, familyTurn, setActor } = useActor();
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState(null);
  // Keep the explanatory 403 while retrying. Each reply belongs to the identifier generation
  // that submitted it; edits invalidate late failures. Cooldowns survive offer unmounts separately.
  const [resendEmail, setResendEmail] = useState(null);
  const identifierGeneration = useRef(0);
  const onIdentifierChange = useCallback(() => {
    identifierGeneration.current += 1;
    setResendEmail(null);
    setAuthError(null);
  }, []);
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState(null);

  // #576 L17 — ask the server who is remembered on this device.
  //
  // EVERY failure lands in the same place, and that is deliberate rather than lazy. A 401 means
  // no account on this device; a 500 or a dead network means we cannot know. Both answers are
  // "do not show a picker", and neither is something to alarm a family with at the front door.
  // `authError` is reserved for a REJECTED CREDENTIAL, because a family told "something went
  // wrong" while typing a working password goes and resets it.
  //
  // The `cancelled` flag is not ceremony: React 18 StrictMode mounts twice in development, and
  // without it the second unmount's late resolve writes to a dead component.
  useEffect(() => {
    let cancelled = false;
    setProfilesLoading(true);
    fetchProfiles()
      // #898: a tile whose tap starts a family turn carries the warning `Login` shows before a digit.
      .then((rows) => {
        if (!cancelled) setProfiles(Array.isArray(rows) ? rows.map((p) => (p.startsTurn ? { ...p, pinWarning: TURN_WARNING } : p)) : []);
      })
      .catch(() => { if (!cancelled) setProfiles([]); })
      .finally(() => { if (!cancelled) setProfilesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // NO "redirect anyone who looks signed in away from this screen" here, deliberately.
  //
  // A version of that shipped in 0f926ec and was withdrawn one review later. It fixed the owner's
  // Google stranding and created a worse one: a fresh sign-in and a DEAD session are the SAME
  // client state — an actor is present and `ActorContext.jsx:101` reports `authenticated` for both
  // — so the guard also caught a family whose session had expired and who had just been told by
  // `SettingsRoute.jsx:100-101` to "sign in again at #/login". They were bounced to `/today`, which
  // 401s, which sends them back here. No form, no way out but clearing cookies.
  //
  // Measured, not argued: `staleActorTrap.test.jsx` passes on the code WITHOUT that effect and
  // fails WITH it.
  //
  // The browser cannot tell the two apart, because the difference lives on the server. So the
  // server says where to go: `server/auth/routes/google.routes.js` now ends a successful callback
  // at `${APP_URL}/#/today` instead of the bare app root.

  const onSignIn = useCallback(
    async ({ email, password }) => {
      const generation = identifierGeneration.current;
      if (!resendEmail) setAuthError(null);
      setSigningIn(true);
      try {
        const actor = await signIn({ identifier: email, password });
        setActor(actor);
        navigate(LANDING_ROUTE, { replace: true });
      } catch (error) {
        if (generation !== identifierGeneration.current) return;
        setAuthError(signInMessage(error));
        setResendEmail(resendAddressFrom(error));
      } finally {
        setSigningIn(false);
      }
    },
    [navigate, setActor, resendEmail],
  );

  // #578 L17/L21 — a child becomes the actor only when the SERVER says so. The actor carries no
  // authority (`toChildActor`); activating a non-parent profile spends the server's parent window.
  const becomeActor = useCallback(
    async (profile, pin) => {
      setPinError(null);
      try {
        const result = pin === undefined ? await activateProfile(profile.id) : await activateProfile(profile.id, pin);
        if (result.outcome === 'ok' && result.familyTurn) {
          // #580 D1: a family turn. The child keeps the turn's generation and the context keeps its
          // state. A locked device holds no actor, so the household comes from the turn it read.
          const household = actor ?? familyTurn?.household ?? null;
          const turn = { ...result.familyTurn, household: household && { tenantId: household.tenantId, tenantSlug: household.tenantSlug } };
          if (turn.state !== 'active') {
            setActor({ actor: null, familyTurn: turn });
            setPinValue('');
            setPinError(PIN_FAULT);
            return;
          }
          setActor({ actor: toChildActor(result.actingProfile, household), familyTurn: turn });
          navigate(LANDING_ROUTE, { replace: true });
          return;
        }
        if (result.outcome === 'ok') {
          // Only the child the SERVER named (`activateProfile` refuses a success that names nobody).
          setActor(toChildActor(result.actingProfile, actor));
          navigate(LANDING_ROUTE, { replace: true });
          return;
        }
        setPinValue('');
        setPinError(result.outcome === 'locked' ? pinLockedMessage(result.retryAfterSeconds) : PIN_REJECTED);
      } catch {
        setPinValue('');
        setPinError(PIN_FAULT);
      }
    },
    [actor, familyTurn, navigate, setActor],
  );

  // A grown-up's tile does nothing here on purpose: the parent proves who they are with the form.
  // A child with no PIN set is in on the tap; a child with a PIN gets the pad. A tile the server
  // would refuse is `none` (`fetchProfiles`), so it never taps in and `Login` shows its notice.
  const onSelectProfile = useCallback(
    (profile) => {
      setPinValue('');
      setPinError(null);
      if (profile?.authMethod === 'open') becomeActor(profile);
    },
    [becomeActor],
  );

  const onPinChange = useCallback((value) => {
    setPinValue(value);
    // A fresh digit is a fresh attempt, so the last setback goes (Login.jsx asks hosts to clear it).
    if (value) setPinError(null);
  }, []);

  const onPinComplete = useCallback((value, profile) => becomeActor(profile, value), [becomeActor]);

  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem' }}>
          <LanternLoader label="Lighting the lanterns — loading the sign-in page." />
        </div>
      }
    >
      <Login
        // #576 L17. This used to be hard-coded `deviceRemembered={false} profiles={[]}`, with a
        // comment deferring the picker to "the display and actor-session work in #32 (R8.1)".
        // That premise expired on 2026-09-21: #685 landed E29-E31 and #691 landed R8.1 itself.
        // The endpoint was live and nothing asked it, so the picker `Login.jsx:5-8` documents
        // could never appear — and L17 requires LIVE AUTHORITY, not a sample list.
        //
        // `deviceRemembered` is derived, never stored. The server already knows: it answers 401
        // when this device carries neither a session nor a display token, and that is exactly
        // what "unknown device" means. So there is no new remembered-device credential here, and
        // nothing about authorization moved into the browser.
        deviceRemembered={profiles.length > 0}
        profiles={profiles}
        loading={profilesLoading}
        onSelectProfile={onSelectProfile}
        pinValue={pinValue}
        onPinChange={onPinChange}
        onPinComplete={onPinComplete}
        pinError={pinError}
        onSignIn={onSignIn}
        signingIn={signingIn}
        authError={authError}
        authErrorAction={resendEmail ? <ResendVerificationAction key={resendEmail} email={resendEmail} /> : null}
        onIdentifierChange={onIdentifierChange}
        // `Login.jsx:552` defaults `providers` to `['google']`, so the button renders whether or
        // not anything answers it. Without this prop `Login.jsx:706` calls nothing, and the
        // screen offers an enabled, focusable control that does not work — reported live
        // 2026-09-10, and the same defect class as the Avatar Studio "New look" button.
        //
        // This is a NAVIGATION, not a fetch: Google sign-in is a server-side redirect flow
        // (#346), mounted at `/api/auth/google` (`server/index.js:156`) behind its own rate
        // limiter (`:142`). `fetch` cannot follow it into the address bar, so the browser goes.
        onProviderSignIn={(key) => window.location.assign(apiUrl(`/api/auth/${key}`))}
      />
    </Suspense>
  );
}
